import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_BASE, getAuthHeader } from "../api/api";

const UserDashboard = () => {
  const [cases, setCases] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [expandedIds, setExpandedIds] = useState([]);
  const [editForm, setEditForm] = useState({
    updates: [],
    new_description: "",
  });

  const fetchCases = async () => {
    const res = await axios.get(`${API_BASE}/user/my-cases`, getAuthHeader());
    setCases(res.data.cases);
  };

  useEffect(() => {
    fetchCases();
    const interval = setInterval(fetchCases, 10000); 
    return () => clearInterval(interval);
  }, []);

  const toggleExpand = (id) => {
    setExpandedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  const startEditing = (c) => {
    setEditingId(c._id);
    setEditForm({
      updates: JSON.parse(JSON.stringify(c.updates || [])),
      new_description: "",
    });
  };

  const submitUpdate = async (id) => {
    try {
      await axios.put(
        `${API_BASE}/user/edit-case`,
        {
          case_id: id,
          updates: editForm.updates,
          new_description: editForm.new_description,
        },
        getAuthHeader(),
      );
      setEditingId(null);
      fetchCases();
    } catch (err) {
      alert(err.response?.data?.message || "Update failed");
    }
  };

  return (
    <div className="max-w-4xl mx-auto relative">
      <h2 className="text-2xl font-bold mb-6">My Assigned Cases</h2>
      <div className="grid gap-4">
        {cases.map((c) => {
          const isExpanded = expandedIds.includes(c._id);
          return (
            <div
              key={c._id}
              className={`bg-white p-6 shadow rounded border-l-8 ${c.is_pending ? "border-red-500" : "border-blue-500"}`}
            >
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-bold text-lg mb-1">{c.title}</h3>
                  <p className="text-[10px] text-gray-400 font-mono mb-4 uppercase">
                    Live since:{" "}
                    {c.scheduled_at
                      ? new Date(c.scheduled_at + "Z").toLocaleString("en-IN")
                      : "Immediate"}
                  </p>
                </div>
                {c.is_pending && (
                  <span className="bg-red-600 text-white text-[10px] px-3 py-1 rounded font-bold animate-pulse">
                    PENDING
                  </span>
                )}
              </div>

              {editingId !== c._id ? (
                <div className="mt-3 mb-4">
                  <button
                    onClick={() => toggleExpand(c._id)}
                    className="mb-2 text-[10px] font-bold text-blue-600 uppercase hover:underline"
                  >
                    {isExpanded ? "Hide Details" : "Show Details"}
                  </button>
                  {isExpanded && (
                    <div className="space-y-2">
                      {c.updates?.map((update, idx) => (
                        <div
                          key={idx}
                          className="p-3 bg-gray-50 border-l-4 border-blue-400 text-sm rounded-r"
                        >
                          <span className="block text-[10px] font-mono text-gray-400 mb-1">
                            {new Date(update.timestamp).toLocaleString()}
                          </span>
                          <span className="text-gray-700 whitespace-pre-wrap">
                            {update.text}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="mb-4 bg-gray-50 p-4 rounded border">
                  <div className="space-y-3 mb-4">
                    {editForm.updates.map((u, idx) => (
                      <textarea
                        key={idx}
                        className="w-full border p-2 rounded text-sm bg-white"
                        value={u.text}
                        onChange={(e) => {
                          const newUpdates = [...editForm.updates];
                          newUpdates[idx].text = e.target.value;
                          setEditForm({ ...editForm, updates: newUpdates });
                        }}
                        rows="2"
                      />
                    ))}
                  </div>
                  <div className="border-t pt-3">
                    <textarea
                      className="w-full border p-2 rounded text-sm mb-3 focus:ring-1 focus:ring-blue-500"
                      placeholder="Add new description entry..."
                      value={editForm.new_description}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          new_description: e.target.value,
                        })
                      }
                      rows="3"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => submitUpdate(c._id)}
                      className="bg-green-600 text-white px-4 py-1.5 rounded text-xs font-bold"
                    >
                      Save All Changes
                    </button>
                    <button
                      onClick={() => setEditingId(null)}
                      className="bg-gray-400 text-white px-4 py-1.5 rounded text-xs font-bold"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              <div className="flex justify-between items-center mt-2 border-t pt-4">
                <span
                  className={`text-xs font-bold px-2 py-1 rounded ${c.is_editable ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}
                >
                  {c.is_editable ? "EDITABLE" : "READ ONLY"}
                </span>
                {c.is_editable && editingId !== c._id && (
                  <button
                    onClick={() => startEditing(c)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 rounded text-sm font-semibold transition-colors"
                  >
                    Update Case
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default UserDashboard;